import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {useState, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {RootState} from '../../../redux/store';
import AurBreadcrumb from '../../common/aur-breadcrumb/AurBreadCrumb';
import AurButton from '../../common/aur-button/AurButton';
import {useNavigate} from 'react-router-dom';
import AurTabMenu from '../../common/aur-tab-menu/AurTabMenu';
import MetaDataFields from './MetaDataFields';
import AnalyzerSettings from './AnalyzerSettings';
import {ReactComponent as GlowRight} from '../../../assests/svg/glow-right.svg';

import {
  getAnalyzers,
  getDocListMetadata,
  getEcaFields,
  getSavedSearchDetails,
} from '../../../api/EcaServices';
import {IFieldModal} from '../../pages/field-management/custom-field/constant';
import {IAnalyzerConfig} from '../model';
import {EciHomePageTabs} from '../../../constants/constants';
import {
  GA_CLICK_TYPES,
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {generatePageAndSection} from '../../../utils/analyticsUtils';

const ViewSettings: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [savedSearchName, setSavedSearchName] = useState<string>('');
  const [nonStandardFields, setNonStandardFields] = useState<IFieldModal[]>([]);
  const [mergedFields, setMergedFields] = useState<IFieldModal[]>([]);
  const [isMetadataLoading, setIsMetadataLoading] = useState(true);

  const navigate = useNavigate();
  const {trackGAEvent} = useAnalytics();

  const getSettingsType = () =>
    indexDetails?.ele?.type === 'Custom'
      ? t('gaTracking.indexSettings.custom')
      : t('gaTracking.indexSettings.standard');

  const getTabName = (index: number) =>
    index === 1
      ? t('gaTracking.indexSettings.analyzerSettings')
      : t('gaTracking.indexSettings.metadataFields');

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
    const settingsType = getSettingsType();
    const tabName = getTabName(index);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: tabName,
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.indexSettings.tabSwitchAction', {
          type: settingsType,
        }),
      },
      subPage: {
        subpage_name: t('gaTracking.indexSettings.settingsSubpage', {
          type: settingsType,
          tab: tabName,
        }),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
  };

  const [availableFields, setAvailableFields] = useState([]);
  const [analyzerData, setAnalyzerData] = useState([]);

  const breadCrumbItems = [
    {
      label: t('eca.ecaHomepage.coreEciHome'),
      path: `/eci-home-page?tab=${EciHomePageTabs.ECI_OVERVIEW}`,
    },
    {
      label: t('ecaIndexing.manageIndexes'),
      path: '/indexmanagement',
    },
    {
      label: t('common.settings'),
    },
  ];
  const indexingType = useSelector(
    (state: RootState) => state.indexes.indexData.ele.name
  );
  const indexDetails = useSelector(
    (state: RootState) => state.indexes.indexData
  );

  const navigateBackToCustomIndex = () => {
    const settingsType = getSettingsType();
    const activeTab = getTabName(selectedTab);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.indexSettings.closeSetting'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.indexSettings.closedSettings', {
          type: settingsType,
        }),
      },
      subPage: {
        subpage_name: t('gaTracking.indexSettings.settingsSubpage', {
          type: settingsType,
          tab: activeTab,
        }),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
    navigate(-1);
  };

  useEffect(() => {
    const mapping = generatePageAndSection(
      window.location.pathname + window.location.search
    );
    trackGAEvent({
      eventName: GA_EVENT_NAMES.PAGE_VIEW,
      page: {
        name: mapping.page,
        section: mapping.section,
        subpage_name: t('gaTracking.indexSettings.settingsSubpage', {
          type: getSettingsType(),
          tab: getTabName(selectedTab),
        }),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
      user: GA_DATA_MODE_AUTO,
    });
  }, []);

  useEffect(() => {
    const loadMetadataFields = async () => {
      setIsMetadataLoading(true);
      await Promise.all([fetchAvailableField(), fetchEcaFields()]);
      setIsMetadataLoading(false);
    };

    loadMetadataFields();
    fetchAnalyzer();
  }, []);

  const {clientId, projectId} = useSelector(
    (state: RootState) => state.reports
  );
  const fieldTemplateId = useSelector(
    (state: RootState) => state.indexes.indexData.ele.fieldTemplateId
  );

  const fetchAvailableField = async () => {
    const params = `?clientId=${clientId}&projectId=${projectId}`;
    try {
      const results = await getDocListMetadata(params, fieldTemplateId);
      setAvailableFields(results.data);
    } catch (e) {
      console.log(e);
    }
  };

  const fetchEcaFields = async () => {
    const params = `clientId=${clientId}&projectId=${projectId}&skip=0&top=2000`;
    try {
      const results = await getEcaFields(params);
      const dataField = results?.data?.data;
      setNonStandardFields(dataField);
    } catch (e) {
      console.log(e);
    }
  };

  const fetchAnalyzer = async () => {
    if (clientId && projectId) {
      try {
        const params = `?clientId=${clientId}&projectId=${projectId}`;
        const result = await getAnalyzers(params, fieldTemplateId);
        const analyzers = result?.data?.data.filter(
          (data: IAnalyzerConfig) => data.isVisible
        );
        setAnalyzerData(analyzers);
      } catch (e) {
        console.error('Error', e);
      }
    }
  };

  useEffect(() => {
    if (availableFields.length && nonStandardFields.length) {
      const fieldNameSet = new Set(
        nonStandardFields.map((field: IFieldModal) => field.fieldLabel)
      );
      const matchedFields = availableFields.filter((field: IFieldModal) =>
        fieldNameSet.has(field.fieldLabel)
      );
      setMergedFields(matchedFields);
    }
  }, [availableFields, nonStandardFields]);

  const tabs = useMemo(
    () => [
      {
        header: {
          text: t('ecaIndexConfig.tabs.metadataFields'),
        },
        content: (
          <MetaDataFields
            availableFields={mergedFields}
            isLoading={isMetadataLoading}
          />
        ),
        index: 0,
      },
      {
        header: {
          text: t('ecaIndexConfig.tabs.analyzerSettings'),
        },
        content: <AnalyzerSettings analyzerData={analyzerData} />,
        index: 1,
      },
    ],
    [mergedFields, analyzerData, isMetadataLoading]
  );

  useEffect(() => {
    const getSavedSearch = async () => {
      if (clientId && projectId) {
        try {
          const params = `?clientId=${clientId}&projectId=${projectId}`;
          const result = await getSavedSearchDetails(
            params,
            indexDetails?.ele?.saveSearchId
          );
          setSavedSearchName(result?.data?.data?.name);
        } catch (e) {
          console.log(e);
        }
      }
    };
    if (indexDetails?.ele?.type === 'Custom') {
      getSavedSearch();
    }
  }, [indexDetails]);

  return (
    <div className="view-settings-container">
      <div className="view-settings-header">
        <Box>
          <AurBreadcrumb items={breadCrumbItems} />
        </Box>
        <GlowRight className="view-settings-glow-effect" />
        <div className="view-settings-index-heading">
          <Typography className="typo-text headline1_size custom-index-heading" component="h1">
            {`${indexingType} ${t('ecaHomepageModals.tabsRightPanel.savedSearch.settingsLabel')}`}
          </Typography>

          <AurButton
            type={'continue'}
            label={t('ecaIndexConfig.buttons.closeSettings')}
            size={'Medium'}
            onClick={navigateBackToCustomIndex}
            ariaDescription={t('ecaIndexConfig.buttons.closeSettingsHint')}
          />
        </div>
        {indexDetails?.ele?.type === 'Custom' && (
          <Typography className="typo-text subhead3_size box-sub-heading">
            {`${t('ecaHomepageModals.tabsRightPanel.savedSearch.builtFrom')} ${savedSearchName}`}
          </Typography>
        )}
        <div className="view-settings-tab-header">
          <AurTabMenu
            headerClasses="typo-text subhead4_size settings-menu-header"
            isResponsive={true}
            width="100%"
            tabs={tabs}
            title={t(
              'ecaHomepageModals.tabsRightPanel.savedSearch.reportTitle'
            )}
            defaultTab={selectedTab}
            onTabChange={handleTabChange}
          />
        </div>
      </div>
    </div>
  );
};

export default ViewSettings;
